import { useState } from "react";

import {
  KNOTS,
  KNOT_CATEGORIES,
  categoryLabel,
  type Knot,
} from "@nav-games/knots";

import { ImageLightbox } from "../../ui/ImageLightbox";

interface KnotsProps {
  onNavigate: (to: string) => void;
}

/// Step-by-step photo guides for each knot. The photos live in
/// apps/web/public/knots/ as transparent PNGs (blue rope on a
/// transparent background, extracted from real photos via the
/// /tmp/process_knots_v4.py colour-keying script). When a knot id
/// has an entry here it surfaces a side-by-side photo strip above
/// the text "How to tie" steps.
interface KnotPhotoStep {
  src: string;
  caption: string;
}

const KNOT_PHOTOS: Record<string, readonly KnotPhotoStep[]> = {
  bowline: [
    {
      src: "/knots/bowline-1.png",
      caption: "Form a small overhand loop in the rope.",
    },
    {
      src: "/knots/bowline-2.png",
      caption: "Pass the working end UP through the loop.",
    },
    {
      src: "/knots/bowline-3.png",
      caption: "Bring it AROUND behind the standing line, then back DOWN through the loop.",
    },
    {
      src: "/knots/bowline-4.png",
      caption: "Pull the working end and standing line to tighten.",
    },
  ],
  reef_knot: [
    {
      src: "/knots/reef-1.png",
      caption: "Lay the two ropes alongside; cross the right end OVER the left.",
    },
    {
      src: "/knots/reef-2.png",
      caption: "Tuck the right end UNDER and bring it back up — first half-knot starting.",
    },
    {
      src: "/knots/reef-3.png",
      caption: "Snug the first half-knot down. Now cross LEFT over right.",
    },
    {
      src: "/knots/reef-4.png",
      caption: "Tuck the second half through, in the opposite direction to the first.",
    },
    {
      src: "/knots/reef-5.png",
      caption: "Pull both standing ends to tighten — finished reef knot.",
    },
  ],
  sheet_bend: [
    {
      src: "/knots/sheet-1.png",
      caption: "Form a bight in the bigger rope (black); pass the smaller rope (blue) up through it from behind.",
    },
    {
      src: "/knots/sheet-2.png",
      caption: "Bring the blue end AROUND behind both legs of the bight and tuck it under itself in front.",
    },
    {
      src: "/knots/sheet-3.png",
      caption: "Pull each rope's standing part to tighten — sheet bend complete.",
    },
  ],
  double_sheet_bend: [
    {
      src: "/knots/sheet-1.png",
      caption: "Form a bight in the bigger rope; pass the smaller rope up through it from behind.",
    },
    {
      src: "/knots/sheet-2.png",
      caption: "Bring the small end around behind both legs of the bight and tuck it under itself in front.",
    },
    {
      src: "/knots/double-3.png",
      caption: "Take a SECOND wrap — bring the small end around behind both legs again.",
    },
    {
      src: "/knots/double-4.png",
      caption: "Tuck the small end under itself a second time across the front.",
    },
    {
      src: "/knots/double-5.png",
      caption: "Pull tight — the extra wrap makes it more secure than a single sheet bend.",
    },
  ],
  figure_eight: [
    {
      src: "/knots/figure-eight-1.png",
      caption: "Form a loop with the working end crossing OVER the standing part.",
    },
    {
      src: "/knots/figure-eight-2.png",
      caption: "Bring the working end BEHIND the standing part and back DOWN through the loop.",
    },
    {
      src: "/knots/figure-eight-3.png",
      caption: "Pull both ends — the classic figure-of-eight stopper.",
    },
  ],
  overhand: [
    {
      src: "/knots/overhand-1.png",
      caption: "Form a loop and pass the working end through it.",
    },
    {
      src: "/knots/overhand-2.png",
      caption: "Pull tight — the simplest stopper. Jams hard under load.",
    },
  ],
  clove_hitch: [
    {
      src: "/knots/clove-1.png",
      caption: "Pass the working end OVER and AROUND the rail; bring it back across the standing part.",
    },
    {
      src: "/knots/clove-2.png",
      caption: "Take it AROUND the rail a second time, going UNDER the diagonal cross.",
    },
    {
      src: "/knots/clove-3.png",
      caption: "Pull both ends to tighten — clove hitch complete.",
    },
  ],
  rolling_hitch: [
    {
      src: "/knots/rolling-1.png",
      caption: "Pass the working end around the spar, crossing OVER the standing part on the load side.",
    },
    {
      src: "/knots/rolling-2.png",
      caption: "Take a second turn beside the first — both turns sit on the load side.",
    },
    {
      src: "/knots/rolling-3.png",
      caption: "Take a third turn on the OTHER side of the standing part and tuck it under to start a half hitch.",
    },
    {
      src: "/knots/rolling-4.png",
      caption: "Work the turns tight together, then load the standing part — it grips and won't slide.",
    },
  ],
  round_turn_two_half_hitches: [
    {
      src: "/knots/round-turn-1.png",
      caption: "Take the working end around the rail TWICE — that's the round turn.",
    },
    {
      src: "/knots/round-turn-2.png",
      caption: "Cross the working end over the standing part and tuck through — first half hitch.",
    },
    {
      src: "/knots/round-turn-3.png",
      caption: "Repeat the same motion in the same direction — second half hitch.",
    },
    {
      src: "/knots/round-turn-4.png",
      caption: "Pull both half hitches snug against the round turn.",
    },
  ],
  cleat_hitch: [
    {
      src: "/knots/cleat-1.png",
      caption: "Take a full turn around the base of the cleat — far horn first, then near.",
    },
    {
      src: "/knots/cleat-2.png",
      caption: "Cross diagonally over the top of the cleat in a figure-of-eight around the horns.",
    },
    {
      src: "/knots/cleat-3.png",
      caption: "Finish with a single locking hitch — loop with the working end tucked through.",
    },
  ],
  timber_hitch: [
    {
      src: "/knots/timber-1.png",
      caption: "Pass the working end around the spar, then once around the standing part.",
    },
    {
      src: "/knots/timber-2.png",
      caption: "Wrap the working end around itself 3+ times, lying along the spar.",
    },
    {
      src: "/knots/timber-3.png",
      caption: "Load the standing part — the wraps trap themselves against the timber.",
    },
  ],
};

/// Foundational rope-handling shapes that come up across many of
/// the knot instructions ("form a bight", "make a twist"). Used
/// in the intro section above the catalogue.
interface ConceptPhoto {
  id: string;
  name: string;
  src: string;
  meaning: string;
}

const ROPE_CONCEPTS: readonly ConceptPhoto[] = [
  {
    id: "bight",
    name: "Bight",
    src: "/knots/bight.png",
    meaning:
      "A U-shaped fold in the rope — the two legs do NOT cross. Many knots start with one.",
  },
  {
    id: "twist",
    name: "Twist (loop)",
    src: "/knots/twist.png",
    meaning:
      "A bight where the working end has been twisted over the standing part — the legs cross.",
  },
];

/// Reference-only catalogue — no flashcards game (yet). Each knot
/// shows its category, use, and step-by-step tying instructions in
/// an expandable card.
export function Knots({ onNavigate }: KnotsProps) {
  const [zoom, setZoom] = useState<{
    src: string;
    alt: string;
    caption?: string;
  } | null>(null);

  const byCat = Object.values(KNOTS).reduce<Record<string, Knot[]>>(
    (acc, k) => {
      (acc[k.category] ??= []).push(k);
      return acc;
    },
    {},
  );

  return (
    <>
      <p className="muted">
        The eight knots required for RYA Day Skipper / Yachtmaster.
        Reference only — tap a knot for the step-by-step tying guide.
        Practice with real rope is the only way to learn these for
        real, but the steps will jog your memory at the chart table.
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Rope-handling basics</h3>
        <p className="small muted" style={{ margin: "0 0 12px" }}>
          Two shapes that come up in almost every knot's instructions.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {ROPE_CONCEPTS.map((c) => (
            <div key={c.id} style={{ textAlign: "center" }}>
              <div
                style={{
                  background: "#faf6e8",
                  border: "1px solid #d4cbb0",
                  borderRadius: 8,
                  padding: 8,
                  aspectRatio: "3 / 2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={c.src}
                  alt={c.name}
                  onClick={() =>
                    setZoom({ src: c.src, alt: c.name, caption: c.meaning })
                  }
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    cursor: "zoom-in",
                  }}
                  loading="lazy"
                />
              </div>
              <div className="small" style={{ marginTop: 6, fontWeight: 600 }}>
                {c.name}
              </div>
              <div className="small muted" style={{ marginTop: 2 }}>
                {c.meaning}
              </div>
            </div>
          ))}
        </div>
      </div>

      {KNOT_CATEGORIES.map((c) => (
        <div key={c}>
          <h2 style={{ marginTop: 24, marginBottom: 8 }}>
            {categoryLabel(c)}
          </h2>
          {(byCat[c] ?? []).map((k) => (
            <details key={k.id} className="card">
              <summary
                style={{
                  cursor: "pointer",
                  listStyle: "none",
                  fontWeight: 600,
                  fontSize: 16,
                }}
              >
                {k.name}
                <div
                  className="small muted"
                  style={{ marginTop: 2, fontWeight: 400 }}
                >
                  {k.use}
                </div>
              </summary>
              <div style={{ marginTop: 12 }}>
                {KNOT_PHOTOS[k.id] && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${KNOT_PHOTOS[k.id]!.length}, minmax(0, 1fr))`,
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    {KNOT_PHOTOS[k.id]!.map((step, i) => (
                      <div key={i} style={{ textAlign: "center" }}>
                        <div
                          style={{
                            position: "relative",
                            background: "#faf6e8",
                            border: "1px solid #d4cbb0",
                            borderRadius: 8,
                            padding: 8,
                            aspectRatio: "3 / 4",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={step.src}
                            alt={`${k.name} step ${i + 1}`}
                            onClick={() =>
                              setZoom({
                                src: step.src,
                                alt: `${k.name} step ${i + 1}`,
                                caption: `${i + 1}. ${step.caption}`,
                              })
                            }
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              cursor: "zoom-in",
                            }}
                            loading="lazy"
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: 4,
                              left: 8,
                              fontSize: 22,
                              fontWeight: 800,
                              color: "#b8302a",
                            }}
                          >
                            {i + 1}.
                          </div>
                        </div>
                        <div
                          className="small"
                          style={{ marginTop: 6, fontWeight: 600 }}
                        >
                          {step.caption}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <strong className="small">How to tie:</strong>
                <ol style={{ marginTop: 6, paddingLeft: 20 }}>
                  {k.steps.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {s}
                    </li>
                  ))}
                </ol>
                {k.notes && (
                  <p className="small muted" style={{ margin: "8px 0 0" }}>
                    <strong>Notes:</strong> {k.notes}
                  </p>
                )}
              </div>
            </details>
          ))}
        </div>
      ))}

      <button
        className="secondary"
        onClick={() => onNavigate("/play/reference")}
        style={{ marginTop: 24 }}
      >
        ← Back to Reference
      </button>

      {zoom && (
        <ImageLightbox
          src={zoom.src}
          alt={zoom.alt}
          {...(zoom.caption ? { caption: zoom.caption } : {})}
          onClose={() => setZoom(null)}
        />
      )}
    </>
  );
}
